import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Mic, MicOff, Download, Trash2, Clock, Users, MessageSquare, Settings, ExternalLink } from "lucide-react";
import { api, pluginApi, recordingApi, serverApi } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

interface VoiceRecordingPluginProps {
  serverId: string;
}

interface VoiceChannel {
  id: string;
  name: string;
  type: number;
}

interface Recording {
  id: number;
  server_id: number;
  channel_id: string;
  channel_name: string;
  initiated_by: string;
  started_at: string;
  ended_at: string | null;
  status: 'recording' | 'completed' | 'failed';
  transcription_count: number;
  participants: string[];
}

interface Transcription {
  id: number;
  recording_id: number;
  user_id: string;
  username: string;
  text: string;
  is_staff: boolean;
  timestamp: string;
}

interface VoiceRecordingConfig {
  geminiApiKey: string;
  autoRecordChannels: string[];
  autoStart: boolean;
  consentFallbackChannelId: string;
  retentionDays: number;
  staffRoles: string[];
  enableSpeakerDiarization: boolean;
}

export function VoiceRecordingPlugin({ serverId }: VoiceRecordingPluginProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data: plugins = [] } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const voiceRecordingPlugin = (plugins as any[]).find((plugin) => plugin.pluginId === 'voice-recording');
  const isEnabled = voiceRecordingPlugin?.enabled ?? false;

  const togglePluginMutation = useMutation({
    mutationFn: async (enabled: boolean) =>
      enabled ? pluginApi.enablePlugin(serverId, 'voice-recording') : pluginApi.disablePlugin(serverId, 'voice-recording'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
      toast.success(t('recording.updated'));
    },
    onError: (error: any) => {
      toast.error(error?.message || t('recording.updateError'));
    },
  });

  // Fetch plugin config
  const { data: config, isLoading: configLoading } = useQuery<VoiceRecordingConfig>({
    queryKey: ["plugin-config", serverId, "voice-recording"],
    queryFn: () => pluginApi.getPluginConfig(serverId, "voice-recording") as Promise<VoiceRecordingConfig>,
  });

  // Fetch voice channels
  const { data: channels = [], isLoading: channelsLoading, isError: channelsError } = useQuery<VoiceChannel[]>({
    queryKey: ["voice-channels", serverId],
    queryFn: async () => {
      const data = await serverApi.getChannels(serverId, 'voice') as any[];
      return data.filter((channel: any) => {
        const channelType = typeof channel.type === 'string' ? channel.type.toUpperCase() : channel.type;
        return channelType === 2 || channelType === 'GUILD_VOICE' || channelType === 'voice';
      });
    },
  });

  const { data: serverChannels = [], isError: serverChannelsError, refetch: refetchServerChannels } = useQuery<any[]>({
    queryKey: ['server-channels', serverId],
    queryFn: () => serverApi.getChannels(serverId, 'text'),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch recordings
  const { data: recordings, isLoading: recordingsLoading } = useQuery<Recording[]>({
    queryKey: ["recordings", serverId],
    queryFn: () => recordingApi.getRecordings(serverId) as Promise<Recording[]>,
  });

  const [selectedRecording, setSelectedRecording] = useState<number | null>(null);

  // Fetch transcription for selected recording
  const { data: transcription, isLoading: transcriptionLoading } = useQuery<Transcription[]>({
    queryKey: ["transcription", selectedRecording],
    queryFn: async () => {
      if (!selectedRecording) return [];
      return recordingApi.getTranscription(selectedRecording) as Promise<Transcription[]>;
    },
    enabled: !!selectedRecording,
  });

  // Update config mutation
  const updateConfig = useMutation({
    mutationFn: async (newConfig: any) => pluginApi.updatePluginConfig(serverId, "voice-recording", newConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plugin-config", serverId, "voice-recording"] });
      toast.success(t('recording.configSaved'));
    },
    onError: () => {
      toast.error(t('recording.configSaveError'));
    },
  });

  // Local state for config
  const [localConfig, setLocalConfig] = useState<VoiceRecordingConfig>({
    geminiApiKey: "",
    autoRecordChannels: [] as string[],
    autoStart: false,
    consentFallbackChannelId: '',
    retentionDays: 30,
    staffRoles: [] as string[],
    enableSpeakerDiarization: true,
  });

  useEffect(() => {
    if (config) {
      setLocalConfig({
        geminiApiKey: config.geminiApiKey || "",
        autoRecordChannels: config.autoRecordChannels || [],
        autoStart: config.autoStart || false,
        consentFallbackChannelId: config.consentFallbackChannelId || '',
        retentionDays: config.retentionDays || 30,
        staffRoles: config.staffRoles || [],
        enableSpeakerDiarization: config.enableSpeakerDiarization !== false,
      });
    }
  }, [config]);

  const handleSaveConfig = () => {
    updateConfig.mutate(localConfig);
  };

  const toggleChannel = (channelId: string) => {
    const updated = localConfig.autoRecordChannels.includes(channelId)
      ? localConfig.autoRecordChannels.filter((id) => id !== channelId)
      : [...localConfig.autoRecordChannels, channelId];

    setLocalConfig({ ...localConfig, autoRecordChannels: updated });
  };

  const formatDuration = (start: string, end: string | null) => {
    if (!end) return t('recording.recordingLive');
    const duration = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}m ${seconds}s`;
  };

  const downloadTranscription = async (recordingId: number) => {
    try {
      const token = api.getToken();
      if (!token) {
        throw new Error(t('recording.authRequired'));
      }

      const response = await fetch(`/api/recordings/${recordingId}/transcription/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(t('recording.downloadError'));

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transcription_${recordingId}.txt`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(t('recording.downloaded'));
    } catch (error) {
      toast.error(t('recording.downloadFailed'));
    }
  };

  const deleteRecording = async (recordingId: number) => {
    try {
      await recordingApi.deleteRecording(recordingId);

      queryClient.invalidateQueries({ queryKey: ["recordings", serverId] });
      toast.success(t('recording.deleted'));
    } catch (error) {
      toast.error(t('recording.deleteFailed'));
    }
  };

  if (configLoading || channelsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">🎙️ {t('recording.title')}</h2>
          <p className="text-muted-foreground">
            {t('recording.subtitle')}
          </p>
        </div>
        <Switch
          checked={isEnabled}
          disabled={togglePluginMutation.isPending}
          onCheckedChange={(enabled) => togglePluginMutation.mutate(enabled)}
        />
      </div>

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings">
            <Settings className="w-4 h-4 mr-2" />
            {t('recording.settings')}
          </TabsTrigger>
          <TabsTrigger value="recordings">
            <Mic className="w-4 h-4 mr-2" />
            {t('recording.recordings')}
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('recording.geminiTitle')}</CardTitle>
              <CardDescription>
                {t('recording.geminiDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="geminiKey">{t('recording.geminiKey')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="geminiKey"
                    type="password"
                    placeholder="AIza..."
                    value={localConfig.geminiApiKey}
                    onChange={(e) =>
                      setLocalConfig({ ...localConfig, geminiApiKey: e.target.value })
                    }
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open("https://makersuite.google.com/app/apikey", "_blank")}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('recording.geminiHelp')}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('recording.speakerDiarization')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('recording.speakerDiarizationDesc')}
                  </p>
                </div>
                <Switch
                  checked={localConfig.enableSpeakerDiarization}
                  onCheckedChange={(checked) =>
                    setLocalConfig({ ...localConfig, enableSpeakerDiarization: checked })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('recording.autoRecordTitle')}</CardTitle>
              <CardDescription>
                {t('recording.autoRecordDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {channels && channels.length > 0 ? (
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-2">
                    {channels.map((channel) => (
                      <div
                        key={channel.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Mic className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{channel.name}</span>
                        </div>
                        <Switch
                          checked={localConfig.autoRecordChannels.includes(channel.id)}
                          onCheckedChange={() => toggleChannel(channel.id)}
                        />
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {channelsError ? 'Impossibile caricare i canali vocali. Verifica che il bot sia nel server.' : t('recording.noVoiceChannels')}
                </p>
              )}

              <div className="flex items-center justify-between pt-2">
                <div className="space-y-0.5">
                  <Label>{t('recording.autoStart')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('recording.autoStartDesc')}
                  </p>
                </div>
                <Switch
                  checked={localConfig.autoStart}
                  onCheckedChange={(checked) =>
                    setLocalConfig({ ...localConfig, autoStart: checked })
                  }
                />
              </div>

              <div className="space-y-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <Label>Canale fallback consenso</Label>
                <Select
                  value={localConfig.consentFallbackChannelId || 'none'}
                  onValueChange={(value) => setLocalConfig({ ...localConfig, consentFallbackChannelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona canale fallback" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleziona un canale fallback</SelectItem>
                    {serverChannels.map((channel: any) => (
                        <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {serverChannelsError ? 'Impossibile caricare i canali testuali. Verifica che il bot sia nel server.' : serverChannels.length === 0 ? 'Nessun canale testuale disponibile. Ricarica la lista o verifica i permessi del bot.' : 'Se i DM sono chiusi, il bot crea qui un thread privato, aggiunge l’utente e invia il consenso.'}
                </p>
                {serverChannels.length === 0 && !serverChannelsError && (
                  <Button type="button" variant="outline" size="sm" onClick={() => refetchServerChannels()}>Ricarica canali</Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('recording.storageTitle')}</CardTitle>
              <CardDescription>
                {t('recording.storageDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="retention">{t('recording.retentionDays')}</Label>
                <Input
                  id="retention"
                  type="number"
                  min="1"
                  max="365"
                  value={localConfig.retentionDays}
                  onChange={(e) =>
                    setLocalConfig({ ...localConfig, retentionDays: parseInt(e.target.value) })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {t('recording.retentionHelp')}
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveConfig} disabled={updateConfig.isPending}>
              {updateConfig.isPending ? t('recording.saving') : t('recording.save')}
            </Button>
          </div>
        </TabsContent>

        {/* Recordings Tab */}
        <TabsContent value="recordings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('recording.recentTitle')}</CardTitle>
              <CardDescription>
                {t('recording.recentDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recordingsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : recordings && recordings.length > 0 ? (
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-3">
                    {recordings.map((recording) => (
                      <div
                        key={recording.id}
                        className="border rounded-lg p-4 space-y-3 hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  recording.status === "recording"
                                    ? "default"
                                    : recording.status === "completed"
                                    ? "secondary"
                                    : "destructive"
                                }
                              >
                                {recording.status === "recording" && <Mic className="w-3 h-3 mr-1 animate-pulse" />}
                                {recording.status === "completed" && <MicOff className="w-3 h-3 mr-1" />}
                                {recording.status.toUpperCase()}
                              </Badge>
                              <span className="font-medium">#{recording.id}</span>
                            </div>
                            <h4 className="font-semibold">{recording.channel_name}</h4>
                          </div>
                          <div className="flex gap-2">
                            {recording.status === "completed" && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedRecording(recording.id)}
                                >
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  {t('recording.view')}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadTranscription(recording.id)}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => {
                                    if (confirm(t('recording.deleteConfirm'))) {
                                      deleteRecording(recording.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            <span>{formatDuration(recording.started_at, recording.ended_at)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span>{recording.participants?.length || 0} {t('recording.participants')}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MessageSquare className="w-4 h-4" />
                            <span>{recording.transcription_count} {t('recording.segments')}</span>
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          {t('recording.started')} {new Date(recording.started_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <Alert>
                  <AlertDescription>
                    {t('recording.empty')}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Transcription View */}
          {selectedRecording && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('recording.transcriptionTitle')} #{selectedRecording}</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedRecording(null)}
                  >
                    {t('recording.close')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {transcriptionLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : transcription && transcription.length > 0 ? (
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-4">
                      {transcription.map((segment) => (
                        <div
                          key={segment.id}
                          className={`p-3 rounded-lg ${
                            segment.is_staff ? "bg-blue-500/10 border-blue-500/20" : "bg-accent"
                          } border`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={segment.is_staff ? "default" : "secondary"}>
                              {segment.is_staff ? t('recording.staff') : t('recording.user')}
                            </Badge>
                            <span className="font-semibold">{segment.username}</span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {new Date(segment.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-sm">{segment.text}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <Alert>
                    <AlertDescription>
                      {t('recording.noTranscription')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* How It Works Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('recording.howItWorks')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h4 className="font-semibold mb-1">{t('recording.step1Title')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('recording.step1Desc')}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h4 className="font-semibold mb-1">{t('recording.step2Title')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('recording.step2Desc')}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h4 className="font-semibold mb-1">{t('recording.step3Title')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('recording.step3Desc')}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h4 className="font-semibold mb-1">{t('recording.step4Title')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('recording.step4Desc')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
